type TableCell = {
  classes: string;
  column_key: string;
  column_title: string;
  colspan: number;
  rowspan: number;
  text?: string;
  value: string;
};

type TableColumn = {
  classes: string;
  key: string;
  title: string;
};

type TablePayload = {
  columns: TableColumn[];
  rows: TableCell[][];
  table_id: number;
  table_type: string;
};

type SortDirection = 'asc' | 'desc';
type SortState = SortDirection | 'none';
type SearchMatchType = 'player' | 'team' | 'position';

type SearchSelection = {
  type: SearchMatchType;
  normalizedValue: string;
  value: string;
};

type SearchSuggestion = SearchSelection;

type TableInstance = {
  activeSortKey: string | null;
  bodyScroll: HTMLElement | null;
  cachedColumnWidths: number[] | null;
  csvDownloadUrl: string | null;
  el: HTMLElement;
  filteredRowIndexes: number[];
  headerScroll: HTMLElement | null;
  label: string;
  orderedRowIndexes: number[];
  payload: TablePayload;
  playerColumnKeys: string[];
  positionColumnKeys: string[];
  rowElements: Map<number, HTMLTableRowElement>;
  teamColumnKeys: string[];
  tierColumnKey: string | null;
  sortButtons: Map<string, HTMLButtonElement>;
  sortDirection: SortState;
  tbody: HTMLTableSectionElement | null;
};

class TablePage {
  private el: HTMLElement;

  private downloadButton: HTMLButtonElement | null;

  private bundleExcelDownload: boolean;

  private panels: HTMLElement[];

  private searchContainer: HTMLElement | null;

  private searchInput: HTMLInputElement | null;

  private searchClearButton: HTMLButtonElement | null;

  private searchSuggestions: HTMLElement | null;

  private searchTags: HTMLElement | null;

  private searchWrap: HTMLElement | null;

  private tables: TableInstance[];

  private searchSelections: SearchSelection[];

  private tableLayoutRefreshFrame = 0;

  constructor(el: HTMLElement) {
    this.el = el;
    this.downloadButton = this.el.querySelector<HTMLButtonElement>('[data-table-download]');
    this.bundleExcelDownload = this.el.hasAttribute('data-bundle-excel-download');
    this.panels = Array.from(this.el.querySelectorAll<HTMLElement>('[data-table-panel]'));
    this.searchContainer = this.el.querySelector<HTMLElement>('[data-table-search]');
    this.searchInput = this.el.querySelector<HTMLInputElement>('[data-table-search-input]');
    this.searchClearButton = this.el.querySelector<HTMLButtonElement>('[data-table-search-clear]');
    this.searchSuggestions = this.el.querySelector<HTMLElement>('[data-table-search-suggestions]');
    this.searchTags = this.el.querySelector<HTMLElement>('[data-table-search-tags]');
    this.searchWrap = this.el.querySelector<HTMLElement>('[data-table-search-wrap]');
    this.tables = Array.from(this.el.querySelectorAll<HTMLElement>('[data-custom-data-table]'))
      .map((tableEl) => this.createTableInstance(tableEl))
      .filter((table): table is TableInstance => table !== null);
    this.searchSelections = [];

    if (!this.tables.length) {
      return;
    }

    this.init();
  }

  private init() {
    this.tables.forEach((table) => {
      table.headerScroll?.querySelectorAll<HTMLElement>('a, button, input, select, textarea, [tabindex]').forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });

      table.el.querySelectorAll<HTMLButtonElement>('[data-sort-button]').forEach((button) => {
        const columnKey = button.dataset.sortKey ?? '';

        if (!columnKey) {
          return;
        }

        button.addEventListener('click', () => {
          this.toggleSort(table, columnKey);
        });
      });

      this.renderTableSortState(table);
      this.renderTableRows(table);
      this.renderTableFilteredState(table);
      this.renderTierGroupState(table);
    });

    this.renderSearchClearButton();
    this.renderSearchTags();
    this.initTableScroll();
    this.syncActiveTableControls();

    this.downloadButton?.addEventListener('click', () => {
      if (this.bundleExcelDownload) {
        this.downloadBundledExcel();
        return;
      }

      const activeTable = this.getActiveTable();

      if (!activeTable) {
        return;
      }

      if (activeTable.csvDownloadUrl) {
        this.downloadUploadedCsv(activeTable);
        return;
      }

      this.downloadCsv(activeTable);
    });

    this.searchInput?.addEventListener('input', () => {
      this.applySearch();
    });

    this.searchInput?.addEventListener('focus', () => {
      this.renderSuggestions();
    });

    this.searchContainer?.addEventListener('focusout', (event) => {
      const nextFocusedEl = event.relatedTarget as Node | null;

      if (nextFocusedEl && this.searchContainer?.contains(nextFocusedEl)) {
        return;
      }

      this.hideSuggestions();
    });

    // Select on pointerdown (before blur) so mobile taps work when relatedTarget is null.
    this.searchSuggestions?.addEventListener('pointerdown', (event) => {
      const target = event.target as HTMLElement | null;
      const suggestionButton = target?.closest<HTMLButtonElement>('[data-search-suggestion]');

      if (!suggestionButton) {
        return;
      }

      // Keep input focused so focusout does not clear suggestions mid-tap.
      event.preventDefault();
      this.selectSearchSuggestion(suggestionButton);
    });

    this.searchSuggestions?.addEventListener('click', (event) => {
      const target = event.target as HTMLElement | null;
      const suggestionButton = target?.closest<HTMLButtonElement>('[data-search-suggestion]');

      if (!suggestionButton) {
        return;
      }

      this.selectSearchSuggestion(suggestionButton);
    });

    this.searchTags?.addEventListener('click', (event) => {
      const target = event.target as HTMLElement | null;
      const removeButton = target?.closest<HTMLButtonElement>('[data-search-tag-remove]');

      if (!removeButton) {
        return;
      }

      this.removeSearchSelection(removeButton.dataset.searchTagKey ?? '');
      this.searchInput?.focus();
    });

    this.searchClearButton?.addEventListener('click', () => {
      this.clearSearch();
      this.searchInput?.focus();
    });

    this.el.querySelectorAll<HTMLElement>('[data-table-tab-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        window.setTimeout(() => {
          this.syncActiveTableControls();
          this.applySearch();
        }, 220);
      });
    });

    this.el.addEventListener('table-tabs:changed', () => {
      this.syncActiveTableControls();
      this.scheduleTableLayoutRefresh();
    });

    document.addEventListener('click', (event) => {
      const target = event.target as Node | null;

      if (!target || this.el.contains(target)) {
        return;
      }

      this.hideSuggestions();
    });
  }

  private createTableInstance(tableEl: HTMLElement): TableInstance | null {
    const payloadScript = tableEl.querySelector<HTMLScriptElement>('[data-table-payload]');

    if (!payloadScript?.textContent) {
      return null;
    }

    let payload: TablePayload;

    try {
      payload = JSON.parse(payloadScript.textContent) as TablePayload;
    } catch (error) {
      // Ignore malformed payloads so the rest of the page can continue working.
      return null;
    }

    if (!Array.isArray(payload.columns) || !Array.isArray(payload.rows)) {
      return null;
    }

    const tbody = tableEl.querySelector<HTMLTableSectionElement>('tbody');
    const headerScroll = tableEl.querySelector<HTMLElement>('[data-table-header-scroll]');
    const bodyScroll = tableEl.querySelector<HTMLElement>('[data-table-body-scroll]');
    const rowElements = new Map<number, HTMLTableRowElement>();
    tableEl.querySelectorAll<HTMLTableRowElement>('tbody > tr[data-row-index]').forEach((rowEl) => {
      const rowIndex = Number.parseInt(rowEl.dataset.rowIndex ?? '', 10);

      if (Number.isNaN(rowIndex)) {
        return;
      }

      rowElements.set(rowIndex, rowEl);
    });

    const sortButtons = new Map<string, HTMLButtonElement>();
    tableEl.querySelectorAll<HTMLButtonElement>('[data-sort-button]').forEach((button) => {
      const columnKey = button.dataset.sortKey ?? '';

      if (!columnKey) {
        return;
      }

      sortButtons.set(columnKey, button);
    });

    const sortableRankColumn = payload.columns.find((column) =>
      ['rank', 'overall'].includes(this.normalizeLabel(column.title)),
    );

    const orderedRowIndexes = payload.rows.map((_, index) => index);
    const initialSortDirection = sortableRankColumn
      ? this.detectInitialSortDirection(payload.rows, sortableRankColumn.key)
      : 'none';
    const playerColumnKeys = payload.columns
      .filter((column) => this.isSearchColumn(column, 'player'))
      .map((column) => column.key);
    const positionColumnKeys = payload.columns
      .filter((column) => this.isSearchColumn(column, 'position'))
      .map((column) => column.key);
    const teamColumnKeys = payload.columns
      .filter((column) => this.isSearchColumn(column, 'team'))
      .map((column) => column.key);

    if (sortableRankColumn && initialSortDirection !== 'none') {
      orderedRowIndexes.splice(
        0,
        orderedRowIndexes.length,
        ...this.getSortedRowIndexes(payload.rows, sortableRankColumn.key, initialSortDirection),
      );
    }

    return {
      activeSortKey: sortableRankColumn && initialSortDirection !== 'none' ? sortableRankColumn.key : null,
      bodyScroll,
      cachedColumnWidths: null,
      csvDownloadUrl: tableEl.dataset.csvDownloadUrl?.trim() || null,
      el: tableEl,
      filteredRowIndexes: [...orderedRowIndexes],
      headerScroll,
      label: tableEl.dataset.tableLabel ?? '',
      orderedRowIndexes,
      payload,
      playerColumnKeys,
      positionColumnKeys,
      rowElements,
      teamColumnKeys,
      tierColumnKey: payload.columns.find((column) => this.isTierColumn(column))?.key ?? null,
      sortButtons,
      sortDirection: initialSortDirection,
      tbody,
    };
  }

  private initTableScroll() {
    this.tables.forEach((table) => {
      const { headerScroll, bodyScroll } = table;

      if (headerScroll && bodyScroll) {
        let syncing = false;
        const syncScroll = (from: HTMLElement, to: HTMLElement) => {
          if (syncing || from.scrollLeft === to.scrollLeft) {
            return;
          }

          syncing = true;
          to.scrollLeft = from.scrollLeft;
          syncing = false;
        };

        headerScroll.addEventListener('scroll', () => syncScroll(headerScroll, bodyScroll), { passive: true });
        bodyScroll.addEventListener('scroll', () => syncScroll(bodyScroll, headerScroll), { passive: true });
      }

      const resizeObserver = new ResizeObserver(() => {
        this.scheduleTableLayoutRefresh();
      });
      resizeObserver.observe(table.el);
    });

    this.scheduleTableLayoutRefresh();
  }

  private syncTableColumnWidths(table: TableInstance) {
    const headerTable = table.headerScroll?.querySelector<HTMLTableElement>('table');
    const bodyTable = table.bodyScroll?.querySelector<HTMLTableElement>('table');

    if (!headerTable || !bodyTable || table.el.dataset.tableSyncingWidths === 'true') {
      return;
    }

    table.el.dataset.tableSyncingWidths = 'true';

    const headerCells = Array.from(headerTable.querySelectorAll<HTMLTableCellElement>('thead th'));
    const headerCols = Array.from(headerTable.querySelectorAll<HTMLTableColElement>('col'));
    const bodyCols = Array.from(bodyTable.querySelectorAll<HTMLTableColElement>('col'));
    const isFiltered = table.el.dataset.tableIsFiltered === 'true';
    const cachedWidths = table.cachedColumnWidths;
    const canReuseCache =
      isFiltered && cachedWidths !== null && cachedWidths.length === headerCells.length;

    let widths: number[];

    if (canReuseCache) {
      widths = [...cachedWidths];
    } else {
      const resetWidth = (element: HTMLElement) => {
        element.style.width = '';
        element.style.minWidth = '';
        element.style.maxWidth = '';
      };

      headerCells.forEach(resetWidth);
      headerCols.forEach(resetWidth);
      bodyCols.forEach(resetWidth);
      // Clear CSS lg:min-w-200 while measuring so the desktop floor is applied
      // from natural content widths, not from an already-stretched layout.
      headerTable.style.width = 'max-content';
      headerTable.style.minWidth = '0';
      headerTable.style.tableLayout = 'auto';
      bodyTable.style.width = 'max-content';
      bodyTable.style.minWidth = '0';
      bodyTable.style.tableLayout = 'auto';

      widths = headerCells.map((headerCell) => {
        const columnKey = headerCell.dataset.columnKey ?? '';
        const bodyCell = columnKey
          ? bodyTable.querySelector<HTMLElement>(
              `tbody tr:not([hidden]) td[data-column-key="${CSS.escape(columnKey)}"]`,
            )
          : null;

        return Math.ceil(Math.max(
          headerCell.getBoundingClientRect().width,
          bodyCell?.getBoundingClientRect().width ?? 0,
        ));
      });
    }

    const totalWidth = this.getSyncedTableWidth(widths);

    if (!isFiltered) {
      table.cachedColumnWidths = [...widths];
    }

    headerTable.style.tableLayout = 'fixed';
    bodyTable.style.tableLayout = 'fixed';
    headerTable.style.width = `${totalWidth}px`;
    headerTable.style.minWidth = '';
    bodyTable.style.width = `${totalWidth}px`;
    bodyTable.style.minWidth = '';

    widths.forEach((width, index) => {
      const widthValue = `${width}px`;

      [headerCols[index], bodyCols[index], headerCells[index]].forEach((element) => {
        if (!element) {
          return;
        }

        element.style.width = widthValue;
        element.style.minWidth = widthValue;
        element.style.maxWidth = widthValue;
      });
    });

    window.requestAnimationFrame(() => {
      delete table.el.dataset.tableSyncingWidths;
    });
  }

  private getSyncedTableWidth(widths: number[]): number {
    const totalWidth = widths.reduce((sum, width) => sum + width, 0);
    const desktopMinWidth = 800;
    const isDesktop = window.matchMedia('(min-width: 64rem)').matches;

    if (!isDesktop || totalWidth <= 0 || totalWidth >= desktopMinWidth || !widths.length) {
      return totalWidth;
    }

    const extraWidth = desktopMinWidth - totalWidth;
    const extraPerColumn = Math.floor(extraWidth / widths.length);
    let remainder = extraWidth - extraPerColumn * widths.length;

    widths.forEach((width, index) => {
      const bonus = extraPerColumn + (remainder > 0 ? 1 : 0);

      if (remainder > 0) {
        remainder -= 1;
      }

      widths[index] = width + bonus;
    });

    return desktopMinWidth;
  }

  private scheduleTableLayoutRefresh() {
    if (this.tableLayoutRefreshFrame) {
      window.cancelAnimationFrame(this.tableLayoutRefreshFrame);
    }

    this.tableLayoutRefreshFrame = window.requestAnimationFrame(() => {
      this.tableLayoutRefreshFrame = 0;
      this.tables.forEach((table) => this.syncTableColumnWidths(table));
    });
  }

  private detectInitialSortDirection(rows: TableCell[][], columnKey: string): SortState {
    const numericValues = rows.map((row) => this.getNumericValueForRow(row, columnKey));

    if (!numericValues.length || numericValues.some((value) => value === null)) {
      return 'none';
    }

    const isAscending = numericValues.every((value, index) => {
      if (index === 0) {
        return true;
      }

      return (value ?? 0) >= (numericValues[index - 1] ?? 0);
    });

    if (isAscending) {
      return 'asc';
    }

    const isDescending = numericValues.every((value, index) => {
      if (index === 0) {
        return true;
      }

      return (value ?? 0) <= (numericValues[index - 1] ?? 0);
    });

    return isDescending ? 'desc' : 'none';
  }

  private getActivePanel(): HTMLElement | null {
    if (!this.panels.length) {
      return null;
    }

    return this.panels.find((panel) => !panel.hasAttribute('hidden')) ?? null;
  }

  private getActiveTable(): TableInstance | null {
    const activePanel = this.getActivePanel();

    if (!activePanel) {
      return this.tables[0] ?? null;
    }

    if (activePanel.dataset.tableDisabled === 'true') {
      return null;
    }

    const activeTableEl = activePanel.querySelector<HTMLElement>('[data-custom-data-table]');

    if (!activeTableEl) {
      return null;
    }

    return this.tables.find((table) => table.el === activeTableEl) ?? null;
  }

  private syncActiveTableControls() {
    const activePanel = this.getActivePanel();
    const isDisabledPanel = activePanel?.dataset.tableDisabled === 'true';
    const hasActiveTable = this.getActiveTable() !== null;
    const showControls = !isDisabledPanel && hasActiveTable;

    if (this.downloadButton) {
      this.downloadButton.hidden = !showControls;
      this.downloadButton.toggleAttribute('disabled', !showControls);
      this.downloadButton.setAttribute('aria-hidden', showControls ? 'false' : 'true');
    }

    if (this.searchWrap) {
      this.searchWrap.hidden = !showControls;
      this.searchWrap.setAttribute('aria-hidden', showControls ? 'false' : 'true');
    }

    if (!showControls) {
      this.hideSuggestions();
    }
  }

  private toggleSort(table: TableInstance, columnKey: string) {
    const nextDirection: SortDirection =
      table.activeSortKey === columnKey && table.sortDirection === 'asc' ? 'desc' : 'asc';

    table.activeSortKey = columnKey;
    table.sortDirection = nextDirection;
    table.orderedRowIndexes = this.getSortedRowIndexes(table.payload.rows, columnKey, nextDirection);
    table.filteredRowIndexes = this.getFilteredRowIndexes(table);

    this.renderTableRows(table);
    this.renderTableSortState(table);
    this.renderTierGroupState(table);
    this.scheduleTableLayoutRefresh();
  }

  private getSortedRowIndexes(
    rows: TableCell[][],
    columnKey: string,
    direction: SortDirection,
  ): number[] {
    return rows
      .map((_, index) => index)
      .sort((leftIndex, rightIndex) => {
        const leftText = this.getCellTextForRow(rows[leftIndex], columnKey);
        const rightText = this.getCellTextForRow(rows[rightIndex], columnKey);
        const leftIsEmpty = leftText === '';
        const rightIsEmpty = rightText === '';

        if (leftIsEmpty && rightIsEmpty) {
          return leftIndex - rightIndex;
        }

        if (leftIsEmpty) {
          return 1;
        }

        if (rightIsEmpty) {
          return -1;
        }

        const leftValue = this.parseNumericValue(leftText);
        const rightValue = this.parseNumericValue(rightText);

        if (leftValue !== null && rightValue !== null) {
          if (leftValue === rightValue) {
            return leftIndex - rightIndex;
          }

          return direction === 'asc' ? leftValue - rightValue : rightValue - leftValue;
        }

        const textCompare = leftText.localeCompare(rightText, undefined, {
          numeric: true,
          sensitivity: 'base',
        });

        if (textCompare === 0) {
          return leftIndex - rightIndex;
        }

        return direction === 'asc' ? textCompare : -textCompare;
      });
  }

  private getCellTextForRow(row: TableCell[], columnKey: string): string {
    const cell = row.find((item) => item.column_key === columnKey);
    const rawText = cell?.text ?? this.stripHtml(cell?.value ?? '');

    return rawText.replace(/\u00a0/g, ' ').trim();
  }

  private getNumericValueForRow(row: TableCell[], columnKey: string): number | null {
    return this.parseNumericValue(this.getCellTextForRow(row, columnKey));
  }

  private parseNumericValue(value: string): number | null {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return null;
    }

    const compactValue = trimmedValue.replace(/\s+/g, '');

    // Only treat wholly numeric strings as numbers. Do not extract digits from
    // labels like player names, teams, or opponents (e.g. "SF 49ers").
    let normalizedValue = compactValue;

    if (/^[+-]?\d{1,3}(,\d{3})+(\.\d+)?$/.test(compactValue)) {
      normalizedValue = compactValue.replace(/,/g, '');
    } else if (/^[+-]?\d{1,3}(\.\d{3})+(,\d+)?$/.test(compactValue)) {
      normalizedValue = compactValue.replace(/\./g, '').replace(',', '.');
    } else if (/^[+-]?\d+([.,]\d+)?$/.test(compactValue)) {
      normalizedValue = compactValue.replace(',', '.');
    } else {
      return null;
    }

    normalizedValue = normalizedValue.replace(/^\+/, '');

    if (!normalizedValue || normalizedValue === '-' || normalizedValue === '.' || normalizedValue === '-.') {
      return null;
    }

    const parsedValue = Number.parseFloat(normalizedValue);

    return Number.isNaN(parsedValue) ? null : parsedValue;
  }

  private renderTableRows(table: TableInstance) {
    if (!table.tbody) {
      return;
    }

    const visibleRowIndexes = new Set(table.filteredRowIndexes);

    table.orderedRowIndexes.forEach((rowIndex) => {
      const rowElement = table.rowElements.get(rowIndex);

      if (rowElement) {
        const isVisible = visibleRowIndexes.has(rowIndex);
        rowElement.hidden = !isVisible;

        if (isVisible) {
          table.tbody?.appendChild(rowElement);
        }
      }
    });
  }

  private renderTableSortState(table: TableInstance) {
    table.sortButtons.forEach((_, columnKey) => {
      const isActive = table.activeSortKey === columnKey && table.sortDirection !== 'none';
      const ariaSort = isActive
        ? table.sortDirection === 'asc'
          ? 'ascending'
          : 'descending'
        : 'none';

      table.el.querySelectorAll<HTMLTableCellElement>(`th[data-column-key="${CSS.escape(columnKey)}"]`).forEach((headerCell) => {
        headerCell.setAttribute('aria-sort', ariaSort);
      });

      table.el.querySelectorAll<HTMLButtonElement>(`[data-sort-button][data-sort-key="${CSS.escape(columnKey)}"]`).forEach((button) => {
        const iconUp = button.querySelector<HTMLElement>('[data-sort-icon-up]');
        const iconDown = button.querySelector<HTMLElement>('[data-sort-icon-down]');

        if (!iconUp || !iconDown) {
          return;
        }

        this.setIconOpacity(iconUp, 'opacity-40');
        this.setIconOpacity(iconDown, 'opacity-40');

        if (!isActive) {
          return;
        }

        if (table.sortDirection === 'asc') {
          this.setIconOpacity(iconUp, 'opacity-100');
          this.setIconOpacity(iconDown, 'opacity-20');
          return;
        }

        this.setIconOpacity(iconUp, 'opacity-20');
        this.setIconOpacity(iconDown, 'opacity-100');
      });
    });
  }

  private downloadUploadedCsv(table: TableInstance) {
    if (!table.csvDownloadUrl) {
      return;
    }

    const link = document.createElement('a');
    const fileName = this.getUploadedFileName(table.csvDownloadUrl);

    link.href = table.csvDownloadUrl;
    if (fileName) {
      link.download = fileName;
    }
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  private getUploadedFileName(url: string): string {
    try {
      const pathname = new URL(url, window.location.origin).pathname;
      const segment = pathname.split('/').pop() ?? '';

      return decodeURIComponent(segment);
    } catch (error) {
      return '';
    }
  }

  private downloadCsv(table: TableInstance) {
    const exportColumns = table.payload.columns.filter(
      (column) => !this.isCsvExcludedColumn(column),
    );
    const csvRows = [
      exportColumns.map((column) => this.escapeCsvValue(column.title)).join(','),
      ...this.getVisibleOrderedRowIndexes(table).map((rowIndex) => {
        const row = table.payload.rows[rowIndex] ?? [];
        const rowCellMap = new Map(row.map((cell) => [cell.column_key, cell]));

        return exportColumns
          .map((column) => {
            const cell = rowCellMap.get(column.key);
            const cellText = cell?.text ?? this.stripHtml(cell?.value ?? '');

            return this.escapeCsvValue(cellText);
          })
          .join(',');
      }),
    ];

    const csvContent = csvRows.join('\r\n');
    const csvBlob = new Blob(['\uFEFF', csvContent], { type: 'text/csv;charset=utf-8;' });
    const csvUrl = URL.createObjectURL(csvBlob);
    const link = document.createElement('a');

    link.href = csvUrl;
    link.download = this.getDownloadFileName(table);
    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(csvUrl);
  }

  private downloadBundledExcel() {
    const downloadUrl = this.el.dataset.bundleExcelUrl?.trim();

    if (!downloadUrl) {
      return;
    }

    const link = document.createElement('a');

    link.href = downloadUrl;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  private isCsvExcludedColumn(column: TableColumn): boolean {
    const excludedTitles = ['+/-', '+/- number'];

    return excludedTitles.includes(this.normalizeLabel(column.title));
  }

  private getDownloadFileName(table: TableInstance): string {
    return `${this.slugifyLabel(table.label || `table-${table.payload.table_id}`)}-${this.getDateSuffix()}.csv`;
  }

  private slugifyLabel(label: string): string {
    const slug = label
      .trim()
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join('-');

    return slug || 'Table';
  }

  private getDateSuffix(): string {
    const now = new Date();

    return `${now.getMonth() + 1}-${now.getDate()}`;
  }

  private escapeCsvValue(value: string): string {
    const normalizedValue = value.replace(/\r?\n|\r/g, ' ').trim();

    return `"${normalizedValue.replace(/"/g, '""')}"`;
  }

  private stripHtml(value: string): string {
    const parser = document.createElement('div');

    parser.innerHTML = value;

    return parser.textContent?.trim() ?? '';
  }

  private normalizeLabel(value: string): string {
    return value.trim().toLowerCase();
  }

  private applySearch() {
    this.tables.forEach((table) => {
      table.filteredRowIndexes = this.getFilteredRowIndexes(table);
      this.renderTableRows(table);
      this.renderTableFilteredState(table);
      this.renderTierGroupState(table);
    });

    this.renderSearchClearButton();
    this.renderSuggestions();
    this.scheduleTableLayoutRefresh();
  }

  private getFilteredRowIndexes(table: TableInstance): number[] {
    const query = this.normalizeSearchValue(this.searchInput?.value ?? '');
    const hasSelections = this.searchSelections.length > 0;

    if (!query && !hasSelections) {
      return [...table.orderedRowIndexes];
    }

    return table.orderedRowIndexes.filter((rowIndex) => {
      const row = table.payload.rows[rowIndex] ?? [];
      const playerValues = this.getSearchValuesForRow(row, table.playerColumnKeys);
      const positionValues = this.getSearchValuesForRow(row, table.positionColumnKeys);
      const teamValues = this.getSearchValuesForRow(row, table.teamColumnKeys);

      if (hasSelections) {
        const matchesSelection = this.searchSelections.some((selection) => {
          const candidateValues = this.getCandidateValuesForSelection(
            selection.type,
            playerValues,
            teamValues,
            positionValues,
          );

          return candidateValues.some(
            (value) => this.normalizeSearchValue(value) === selection.normalizedValue,
          );
        });

        if (matchesSelection) {
          return true;
        }
      }

      if (!query) {
        return false;
      }

      return [...playerValues, ...teamValues, ...positionValues].some((value) =>
        this.normalizeSearchValue(value).includes(query),
      );
    });
  }

  private getVisibleOrderedRowIndexes(table: TableInstance): number[] {
    const visibleRowIndexes = new Set(table.filteredRowIndexes);

    return table.orderedRowIndexes.filter((rowIndex) => visibleRowIndexes.has(rowIndex));
  }

  private renderTableFilteredState(table: TableInstance) {
    const isFiltered = this.hasActiveSearchQuery();

    table.el.dataset.tableIsFiltered = isFiltered ? 'true' : 'false';
  }

  private renderTierGroupState(table: TableInstance) {
    table.el.querySelectorAll<HTMLElement>('[data-tier-value]').forEach((cell) => {
      cell.classList.remove('bg-[#D9D9D9]');
    });

    if (!table.tierColumnKey) {
      return;
    }

    let previousTierValue: string | null = null;
    let groupIndex = -1;

    this.getVisibleOrderedRowIndexes(table).forEach((rowIndex) => {
      const row = table.payload.rows[rowIndex] ?? [];
      const tierValue = this.getTierValueForRow(row, table.tierColumnKey ?? '');

      if (!tierValue) {
        previousTierValue = null;
        return;
      }

      if (tierValue !== previousTierValue) {
        groupIndex += 1;
        previousTierValue = tierValue;
      }

      if (groupIndex % 2 !== 1) {
        return;
      }

      table.rowElements
        .get(rowIndex)
        ?.querySelectorAll<HTMLElement>(`[data-column-key="${CSS.escape(table.tierColumnKey ?? '')}"]`)
        .forEach((cell) => {
          cell.classList.add('bg-[#D9D9D9]');
        });
    });
  }

  private renderSuggestions() {
    if (!this.searchSuggestions || !this.searchContainer?.contains(document.activeElement)) {
      return;
    }

    const query = this.normalizeSearchValue(this.searchInput?.value ?? '');

    if (!query) {
      this.hideSuggestions();
      return;
    }

    const suggestions = this.getSuggestions(this.getActiveTable(), query);
    const emptyMessage = query.length < 2
      ? 'There are no matching teams or positions'
      : 'No matching players, teams, or positions';

    if (!suggestions.length) {
      this.searchSuggestions.innerHTML = `
        <div class="border-2 border-black bg-white px-4 py-3 font-mono text-sm leading-none text-black">
          ${emptyMessage}
        </div>
      `;
      this.searchSuggestions.hidden = false;
      return;
    }

    this.searchSuggestions.innerHTML = suggestions
      .map(
        (suggestion) => `
          <button
            class="flex w-full items-center justify-between gap-3 border-x-2 border-black bg-white px-4 py-3 text-left font-mono text-sm leading-none text-black transition-colors hover:bg-black hover:text-white focus:bg-black focus:text-white focus:outline-none first:border-t-2 first:border-t-black last:border-b-2 last:border-b-black"
            type="button"
            data-search-suggestion
            data-search-suggestion-type="${suggestion.type}"
            data-search-suggestion-value="${this.escapeAttribute(suggestion.value)}"
          >
            <span>${this.escapeHtml(suggestion.value)}</span>
            <span class="text-[0.625rem] uppercase tracking-[0.12em]">${suggestion.type}</span>
          </button>
        `,
      )
      .join('');
    this.searchSuggestions.hidden = false;
  }

  private getSuggestions(table: TableInstance | null, query: string): SearchSuggestion[] {
    if (!table) {
      return [];
    }

    const suggestionMap = new Map<string, SearchSuggestion>();
    const includePlayerSuggestions = query.length >= 2;

    table.payload.rows.forEach((row) => {
      if (includePlayerSuggestions) {
        this.getSearchValuesForRow(row, table.playerColumnKeys).forEach((value) => {
          this.addSuggestion(suggestionMap, value, 'player', query);
        });
      }

      this.getSearchValuesForRow(row, table.teamColumnKeys).forEach((value) => {
        this.addSuggestion(suggestionMap, value, 'team', query);
      });

      this.getSearchValuesForRow(row, table.positionColumnKeys).forEach((value) => {
        this.addSuggestion(suggestionMap, value, 'position', query);
      });
    });

    const typePriority: Record<SearchMatchType, number> = {
      position: 0,
      team: 1,
      player: 2,
    };

    return Array.from(suggestionMap.values())
      .filter((suggestion) => !this.hasSearchSelection(suggestion.type, suggestion.normalizedValue))
      .sort((left, right) => {
        const byType = typePriority[left.type] - typePriority[right.type];

        return byType !== 0 ? byType : left.value.localeCompare(right.value);
      })
      .slice(0, 8);
  }

  private addSuggestion(
    suggestionMap: Map<string, SearchSuggestion>,
    value: string,
    type: SearchMatchType,
    query: string,
  ) {
    const normalizedValue = this.normalizeSearchValue(value);

    if (!normalizedValue || !normalizedValue.includes(query)) {
      return;
    }

    const key = `${type}:${normalizedValue}`;

    if (suggestionMap.has(key)) {
      return;
    }

    suggestionMap.set(key, {
      normalizedValue,
      type,
      value,
    });
  }

  private getSearchValuesForRow(row: TableCell[], columnKeys: string[]): string[] {
    if (!columnKeys.length) {
      return [];
    }

    const values = new Set<string>();

    row.forEach((cell) => {
      if (!columnKeys.includes(cell.column_key)) {
        return;
      }

      const value = (cell.text ?? this.stripHtml(cell.value)).trim();

      if (value) {
        values.add(value);
      }
    });

    return Array.from(values);
  }

  private isSearchColumn(column: TableColumn, type: SearchMatchType): boolean {
    const haystack = this.normalizeColumnIdentifier(`${column.key} ${column.title}`);
    const teamColumnTokens = ['team', 'player team'];
    const positionColumnTokens = ['position', 'player position', 'pos'];

    if (type === 'player') {
      const playerColumnTokens = [
        'player',
        'quarterback',
        'running back',
        'wide receiver',
        'tight end',
        'defense',
        'flex',
        'qb',
        'rb',
        'wr',
        'te',
        'dst',
      ];

      if (teamColumnTokens.some((token) => haystack.includes(token))) {
        return false;
      }

      return playerColumnTokens.some((token) => haystack.includes(token));
    }

    if (type === 'position') {
      return positionColumnTokens.some((token) => haystack.includes(token));
    }

    return teamColumnTokens.some((token) => haystack.includes(token));
  }

  private getCandidateValuesForSelection(
    type: SearchMatchType,
    playerValues: string[],
    teamValues: string[],
    positionValues: string[],
  ): string[] {
    if (type === 'player') {
      return playerValues;
    }

    if (type === 'position') {
      return positionValues;
    }

    return teamValues;
  }

  private isSearchMatchType(value: string | undefined): value is SearchMatchType {
    return value === 'player' || value === 'team' || value === 'position';
  }

  private normalizeColumnIdentifier(value: string): string {
    return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ');
  }

  private normalizeSearchValue(value: string): string {
    return value.trim().toLowerCase().replace(/\s+/g, ' ');
  }

  private hasActiveSearchQuery(): boolean {
    return (
      this.searchSelections.length > 0 ||
      Boolean(this.normalizeSearchValue(this.searchInput?.value ?? ''))
    );
  }

  private getSearchSelectionKey(type: SearchMatchType, normalizedValue: string): string {
    return `${type}:${normalizedValue}`;
  }

  private hasSearchSelection(type: SearchMatchType, normalizedValue: string): boolean {
    const key = this.getSearchSelectionKey(type, normalizedValue);

    return this.searchSelections.some(
      (selection) => this.getSearchSelectionKey(selection.type, selection.normalizedValue) === key,
    );
  }

  private isTierColumn(column: TableColumn): boolean {
    return this.normalizeColumnIdentifier(`${column.key} ${column.title}`).includes('tier');
  }

  private getTierValueForRow(row: TableCell[], columnKey: string): string | null {
    const cell = row.find((item) => item.column_key === columnKey);
    const value = (cell?.text ?? this.stripHtml(cell?.value ?? '')).trim();

    return value || null;
  }

  private selectSearchSuggestion(suggestionButton: HTMLButtonElement) {
    const suggestionValue = suggestionButton.dataset.searchSuggestionValue ?? '';
    const suggestionType = suggestionButton.dataset.searchSuggestionType;

    if (!suggestionValue || !this.isSearchMatchType(suggestionType)) {
      return;
    }

    const normalizedValue = this.normalizeSearchValue(suggestionValue);

    if (!this.hasSearchSelection(suggestionType, normalizedValue)) {
      this.searchSelections.push({
        normalizedValue,
        type: suggestionType,
        value: suggestionValue,
      });
    }

    if (this.searchInput) {
      this.searchInput.value = '';
    }

    this.renderSearchTags();
    this.applySearch();
    this.hideSuggestions();
  }

  private removeSearchSelection(selectionKey: string) {
    if (!selectionKey) {
      return;
    }

    this.searchSelections = this.searchSelections.filter(
      (selection) =>
        this.getSearchSelectionKey(selection.type, selection.normalizedValue) !== selectionKey,
    );

    this.renderSearchTags();
    this.applySearch();
  }

  private renderSearchTags() {
    if (!this.searchTags) {
      return;
    }

    if (!this.searchSelections.length) {
      this.searchTags.innerHTML = '';
      this.searchTags.hidden = true;
      return;
    }

    this.searchTags.innerHTML = this.searchSelections
      .map((selection) => {
        const selectionKey = this.getSearchSelectionKey(selection.type, selection.normalizedValue);

        return `
          <span class="inline-flex max-w-full items-center gap-2 border-2 border-black bg-white/80 py-1 pl-2.5 pr-1 font-mono text-xs leading-none text-black">
            <span class="truncate">${this.escapeHtml(selection.value)}</span>
            <span class="shrink-0 text-[0.5625rem] uppercase tracking-[0.12em] text-black/55">${selection.type}</span>
            <button
              class="inline-flex h-5 w-5 shrink-0 items-center justify-center text-black transition-colors hover:text-blue focus:outline-none"
              type="button"
              aria-label="${this.escapeAttribute(`Remove ${selection.value}`)}"
              data-search-tag-remove
              data-search-tag-key="${this.escapeAttribute(selectionKey)}"
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M1 1L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                <path d="M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </button>
          </span>
        `;
      })
      .join('');
    this.searchTags.hidden = false;
  }

  private hideSuggestions() {
    if (!this.searchSuggestions) {
      return;
    }

    this.searchSuggestions.hidden = true;
    this.searchSuggestions.innerHTML = '';
  }

  private clearSearch() {
    if (this.searchInput) {
      this.searchInput.value = '';
    }

    this.searchSelections = [];
    this.renderSearchTags();
    this.applySearch();
    this.hideSuggestions();
  }

  private renderSearchClearButton() {
    if (!this.searchClearButton) {
      return;
    }

    const hasValue = this.hasActiveSearchQuery();

    this.searchClearButton.hidden = !hasValue;
    this.searchClearButton.classList.toggle('hidden', !hasValue);
    this.searchClearButton.classList.toggle('inline-flex', hasValue);
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private escapeAttribute(value: string): string {
    return this.escapeHtml(value);
  }

  private setIconOpacity(el: HTMLElement, opacityClass: 'opacity-20' | 'opacity-40' | 'opacity-100') {
    el.classList.remove('opacity-20', 'opacity-40', 'opacity-100');
    el.classList.add(opacityClass);
  }
}

export default TablePage;
